import React, { FC } from 'react'
import { get } from 'lodash'
import { Popover, Position } from '@blueprintjs/core'
import { ApolloError } from 'apollo-boost'
import { Avatar, Button, Flex, Toaster } from '@ui'
import { useReload } from '@hooks/reloadContext'
import EmployeePopoverList from './EmployeePopoverList'
import { isNeighborhood, isShared } from '@utils'
import api, {
  ICatalogEmployee,
  IFloorMapSpace,
  IFullEmployee,
  INeighborhood,
  ISpaceAssignment,
  IUsageType,
} from '@api'
import css from './spaceAssignmentSelect.module.scss'
import { useFloor } from '@providers/mapProvider'

interface AssignmentSelect {
  assignmentPlanId: string
  space: IFloorMapSpace
  onChange: (spaces: IFloorMapSpace[]) => void
}

const AssignmentSelect: FC<AssignmentSelect> = ({ assignmentPlanId, onChange, space }) => {
  const reload = useReload()
  const floor = useFloor()

  const updateSpaceUsage = (usageType: IUsageType) => {
    const nextSpaces = [
      {
        ...space,
        usageType,
        spaceAssignment: {
          ...(space.spaceAssignment as ISpaceAssignment),
          employee: null,
          neighborhood: null,
        },
      },
    ]

    if (space) {
      onChange(nextSpaces)

      api.space
        .batchUpdateUsageTypes(
          nextSpaces.map(({ id }) => id),
          usageType.name
        )
        .then(() => {
          reload.neighborhoods()
          Toaster.success({
            message:
              nextSpaces.length === 1
                ? `${nextSpaces[0].code} changed to ${usageType.displayName}`
                : `${nextSpaces.length} spaces changed to ${usageType.displayName}`,
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    }
  }

  const assign = ({
    toSpaceId,
    fromSpaceId,
    employee,
  }: {
    toSpaceId: string
    fromSpaceId?: string
    employee: ICatalogEmployee
  }) => {
    if (!space) return
    const shared = isShared(space.usageType)
    const neighborhood = isNeighborhood(space.usageType)
    onChange([
      {
        ...space,
        spaceAssignment: {
          ...space.spaceAssignment,
          employee: shared || neighborhood ? null : { ...employee, neighborhoods: [] },
          sharedEmployees: shared
            ? [...(get(space, 'spaceAssignment.sharedEmployees') ?? []), employee]
            : null,
          department: employee.dept,
          team: employee.team,
          neighborhood: neighborhood
            ? ({
                ...space.spaceAssignment.neighborhood,
                employees: neighborhood
                  ? ([
                      ...(get(space, 'spaceAssignment.neighborhood.employees') ?? []),
                      employee,
                    ] as IFullEmployee[])
                  : [],
              } as INeighborhood)
            : null,
        },
      },
    ])
    if (neighborhood) {
      api.neighborhood
        .addEmployees(space?.spaceAssignment?.neighborhood?.id as string, [employee.id])
        .then((neighborhood) => {
          Toaster.success({
            message: `${employee.name} was associated to neighborhood ${neighborhood?.name}`,
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    } else {
      api.space
        .seat({
          assignmentPlanId,
          toSpaceId,
          fromSpaceId,
          employeeId: employee.id,
          moveType: 'move',
        })
        .then(() => {
          reload.employeeCatalog()
          floor.reloadMap?.()
          Toaster.success({
            message: `Moved ${employee.name} to a ${space.code}`,
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    }
  }

  return (
    <div className={css.employees_list}>
      <Popover minimal usePortal={false} position={Position.BOTTOM}>
        <Button
          ghost
          rightIconName="chevron-down"
          text={<SpaceAssignmentEl spaceAssignment={space ? space.spaceAssignment : null} />}
        />
        <EmployeePopoverList
          assignmentPlanId={assignmentPlanId}
          space={space}
          assign={assign}
          updateSpaceUsage={updateSpaceUsage}
        />
      </Popover>
    </div>
  )
}

export default AssignmentSelect

function SpaceAssignmentEl({ spaceAssignment }: { spaceAssignment: ISpaceAssignment | null }) {
  const employee = get(spaceAssignment, 'employee')

  if (!employee) {
    return <span>Employee</span>
  }

  return (
    <Flex align="center" style={{ position: 'relative' }}>
      <Avatar name={employee.name} assets={employee.assets} size={Avatar.Size.XXS} />
      <div style={{ marginLeft: 10 }}>{employee.name}</div>
    </Flex>
  )
}
